import React, { Fragment } from "react";
import styled from "styled-components";
import { Flex, Box } from "grid-styled";
import { withRouter } from "react-router-dom";
import moment from "moment";
import Moment from "react-moment";
import PropTypes from "prop-types";
import { gql } from "apollo-boost";
import { graphql, compose } from "react-apollo";
import ServiceQuery from "../../queries/Service";
import {
  orderByArray,
  getProductionSteps,
  getCurrentProductionStep
} from "../../utils/helpers";
import { addToCache } from "../../utils/queryHelpers";
import ChangeStatusMutation from "../../mutations/ChangeStatus";
import Button from "../Button";
import Modal from "../Modal";
import StatusLabel from "../StatusLabel";
import ServiceOptionsForm from "../services/ServiceOptionsForm";
import ProductionStepCompleteForm from "../services/ProductionStepCompleteForm";
import AttachmentsChecklist from "./AttachmentsChecklist";
import Option from "../Option";
import * as colors from "../../settings/colors";
import {
  JobActivitiesQuery,
  ServiceActivitiesQuery,
  fragments as activityFragments
} from "../../queries/Activities";

const SidebarBox = Box.extend`
  border-bottom: 1px solid ${colors.DIVIDER};

  &:last-child {
    border-bottom: 0;
  }
`;

const SidebarHeader = styled.h3`
  margin: 0 0 8px 0;
  font-size: 14px;
  text-transform: uppercase;
  font-weight: 700;
`;

const StepSecondary = styled.small`
  display: block;
  font-size: 12px;
  line-height: 1;
  margin-bottom: 3px;
  color: ${colors.TEXT_SECONDARY};
`;

class ServiceSidebar extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      showingServiceCancelModal: false,
      showingOptionsChangeModal: false,
      showingStartProductionStepModal: false,
      showingCompleteProductionStepModal: false,
      currentProductionStep: {}
    };
  }

  closeModals = () => {
    this.setState({
      showingServiceCancelModal: false,
      showingOptionsChangeModal: false,
      showingStartProductionStepModal: false,
      showingCompleteProductionStepModal: false,
      currentProductionStep: {}
    });
  };

  renderCurrentStatusBox = () => {
    const { service } = this.props;
    return (
      <SidebarBox py={2}>
        <StatusLabel status={service.status} size="large" />
        {service.status === "on_hold" && (
          <Box pt={1}>
            <b>{service.holdType}</b>
            <br />
            {service.holdReason}
          </Box>
        )}
        {service.status === "awaiting_review" && (
          <Box pt={1}>
            <b>Submitted On:</b>
            <br />
            <Moment format="MMM D, YYYY">{service.submittedAt}</Moment>
          </Box>
        )}
        {service.status === "in_production" && (
          <Box pt={1}>
            <b>Estimated Turnaround Date:</b>
            <br />
            <Moment format="MMM D, YYYY" utc>
              {service.postedTurnaroundDate}
            </Moment>
          </Box>
        )}
      </SidebarBox>
    );
  };

  renderServiceOptionsBox = () => {
    const { service } = this.props;
    const { serviceType, options } = service;

    const serviceLevels = {
      1: "Plans, Specs & MTO",
      2: "Plans & Specs (No MTO)",
      3: "Review Only"
    };

    const newSaleOptions = {
      fullDetailPackage: "Full Detail Package",
      includeElevations: "Include Elevations",
      rush: "Rush",
      rushMaterialTakeoff: "Rush Material Takeoff",
      structuralCalculations: "Structural Calculations",
      pdfStructuralDrawings: "PDF Structural Drawings",
      consultationRequest: "Consultation Request"
    };

    if (["New Sale", "Sales Drawing", "Redraw"].indexOf(serviceType) === -1)
      return null;

    return (
      <SidebarBox style={{ position: "relative" }} py={2}>
        <Button
          type="button"
          onClick={() => this.setState({ showingOptionsChangeModal: true })}
          style={{ position: "absolute", right: 0 }}
        >
          Edit
        </Button>
        {serviceType === "New Sale" && (
          <Fragment>
            <Box pb={1}>
              <div>
                <b>Level {"I".repeat(options.serviceLevel)}</b>
              </div>
              <div style={{ color: colors.TEXT_SECONDARY }}>
                {serviceLevels[options.serviceLevel]}
              </div>
            </Box>
            {Object.keys(newSaleOptions).map(
              key =>
                options[key] && (
                  <Option checked key={key}>
                    {newSaleOptions[key]}
                  </Option>
                )
            )}
          </Fragment>
        )}
        {(serviceType === "Sales Drawing" || serviceType === "Redraw") && (
          <Fragment>
            <Option checked>{options.drawingDepth} drawing depth</Option>
            {options.pdfDrawings && <Option checked>PDF Drawings</Option>}
          </Fragment>
        )}
        <Modal
          title="Changing Service Options..."
          isOpen={this.state.showingOptionsChangeModal}
          close={this.closeModals}
          style={{ maxWidth: 600 }}
        >
          <Box p={2}>
            <ServiceOptionsForm
              cancel={this.closeModals}
              service={this.props.service}
              onSubmit={values =>
                this.props.updateService(values).then(this.closeModals)
              }
            />
          </Box>
        </Modal>
      </SidebarBox>
    );
  };

  renderAttachmentsChecklistBox = () => {
    return (
      <SidebarBox py={2}>
        <SidebarHeader>Checklist</SidebarHeader>
        <AttachmentsChecklist
          service={this.props.service}
          requirements={this.props.attachmentRequirements}
        />
      </SidebarBox>
    );
  };

  completeProductionStep = (currentStep, comment) => {
    const commentTitle = `### ${currentStep.name} notes:`;
    const fullComment = commentTitle + "\n\n" + comment;

    return Promise.all([
      this.props.completeProductionStep(currentStep.value),
      comment
        ? this.props.addComment({
            serviceId: this.props.service.id,
            input: {
              body: fullComment,
              public: false
            }
          })
        : Promise.resolve()
    ]);
  };

  renderCompletedProductionStep = ({ productionStep: cps, service }) => {
    const stepInfo = getProductionSteps(service)[cps.stepType];
    return (
      stepInfo && (
        <Option checked key={cps.stepType}>
          {stepInfo.name}
          {cps.user && <StepSecondary>{cps.user.name}</StepSecondary>}
          <StepSecondary>
            {moment(cps.completedAt).format("MM/DD/YYYY [at] h:mm a")}
          </StepSecondary>
        </Option>
      )
    );
  };

  // TODO: This needs to be so much prettier
  renderProductionStepsBox = () => {
    const { service } = this.props;

    const { productionSteps } = service;
    const orderedSteps = orderByArray(
      productionSteps,
      Object.keys(getProductionSteps(service)),
      "stepType"
    );

    if (!orderedSteps) return null;

    const completedSteps = orderedSteps.filter(ps => ps.completedAt);
    const currentStep = getCurrentProductionStep(
      {
        ...service,
        productionSteps: orderedSteps
      },
      true
    );

    return (
      <SidebarBox py={2}>
        <SidebarHeader>Production Steps</SidebarHeader>
        {completedSteps.length > 0 && (
          <Box>
            {completedSteps.map(cps =>
              this.renderCompletedProductionStep({
                productionStep: cps,
                service
              })
            )}
          </Box>
        )}
        {currentStep &&
          currentStep.info && (
            <Box pt={2}>
              <Box pb={1}>
                <b>{currentStep.info.name}</b>
              </Box>
              {currentStep.action === "start" && (
                <Button
                  primary
                  type="button"
                  onClick={() =>
                    this.setState({
                      showingStartProductionStepModal: true,
                      currentProductionStep: currentStep.info
                    })
                  }
                >
                  Start Step
                </Button>
              )}
              {currentStep.action === "complete" && [
                <div key={1}>
                  Started at{" "}
                  <Moment format="MMM D, YYYY [at] h:mm a">
                    {currentStep.startedAt}
                  </Moment>
                </div>,
                <Button
                  key={2}
                  primary
                  type="button"
                  onClick={() =>
                    this.setState({
                      showingCompleteProductionStepModal: true,
                      currentProductionStep: currentStep.info
                    })
                  }
                >
                  Complete Step
                </Button>
              ]}
            </Box>
          )}
        <Modal
          title="Starting Production Step..."
          isOpen={this.state.showingStartProductionStepModal}
          close={this.closeModals}
        >
          <Box p={2}>
            <p>
              Are you sure you want to start{" "}
              {this.state.currentProductionStep.name || "this production step"}?
            </p>
            <Flex justify="flex-end">
              <Button type="button" onClick={this.closeModals}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                style={{ marginLeft: 4 }}
                onClick={() =>
                  this.props
                    .startProductionStep(this.state.currentProductionStep.value)
                    .then(this.closeModals)
                }
              >
                Start{" "}
                {this.state.currentProductionStep.name || "Production Step"}
              </Button>
            </Flex>
          </Box>
        </Modal>
        <Modal
          title="Completing Production Step..."
          isOpen={this.state.showingCompleteProductionStepModal}
          close={this.closeModals}
        >
          <Box p={2}>
            <ProductionStepCompleteForm
              serviceId={this.props.service ? this.props.service.id : ""}
              currentStep={this.state.currentProductionStep}
              onCancel={this.closeModals}
              onSubmit={comment =>
                this.completeProductionStep(
                  this.state.currentProductionStep,
                  comment
                ).then(this.closeModals)
              }
            />
          </Box>
        </Modal>
      </SidebarBox>
    );
  };

  renderCancelServiceBox = () => {
    const closeModal = () =>
      this.setState({ showingServiceCancelModal: false });
    const cancelService = () => this.props.cancelService().then(closeModal);

    return (
      <SidebarBox pt="64px">
        <Button
          danger
          onClick={() => this.setState({ showingServiceCancelModal: true })}
        >
          Cancel Service
        </Button>
        <Modal
          title="Cancelling Service..."
          isOpen={this.state.showingServiceCancelModal}
          close={closeModal}
        >
          <Box p={2}>
            <p>Are you sure you want to cancel this service request?</p>
            <Flex justify="flex-end">
              <Button type="button" onClick={closeModal}>
                Cancel
              </Button>
              <Button
                type="button"
                danger
                style={{ marginLeft: 4 }}
                onClick={cancelService}
              >
                Cancel Service
              </Button>
            </Flex>
          </Box>
        </Modal>
      </SidebarBox>
    );
  };

  render() {
    const { service, match } = this.props;
    const isAdmin = match.path.match(/^\/admin/);

    return (
      <Box pt={2}>
        {this.renderCurrentStatusBox()}
        {this.renderServiceOptionsBox()}
        {isAdmin && this.renderProductionStepsBox()}
        {this.renderAttachmentsChecklistBox()}
        {service.status !== "cancelled" && this.renderCancelServiceBox()}
      </Box>
    );
  }

  static propTypes = {
    service: PropTypes.shape({
      id: PropTypes.string
    }).isRequired
  };
}

const StartProductionStepMutation = gql`
  mutation StartProductionStep($stepType: String!, $serviceId: ID!) {
    startProductionStep(stepType: $stepType, serviceId: $serviceId) {
      id
      stepType
      startedAt
      completedAt
      user {
        id
        name
        email
      }
    }
  }
`;

const CompleteProductionStepMutation = gql`
  mutation CompleteProductionStep($stepType: String!, $serviceId: ID!) {
    completeProductionStep(stepType: $stepType, serviceId: $serviceId) {
      id
      stepType
      startedAt
      completedAt
      user {
        id
        name
        email
      }
    }
  }
`;

const AddCommentMutation = gql`
  mutation addComment($serviceId: ID!, $input: ActivityInput!) {
    addComment(serviceId: $serviceId, input: $input) {
      ...ActivityFeedActivity
    }
  }
  ${activityFragments.activity}
`;

const enhance = compose(
  withRouter,
  graphql(StartProductionStepMutation, {
    props({ ownProps: { service }, mutate }) {
      return {
        startProductionStep(stepType) {
          return mutate({
            variables: {
              serviceId: service.id,
              stepType
            },
            update: (store, { data: { startProductionStep } }) => {
              addToCache(
                startProductionStep,
                store,
                "service.productionSteps",
                {
                  query: ServiceQuery,
                  variables: { id: service.id }
                }
              );
            }
          });
        }
      };
    }
  }),
  graphql(CompleteProductionStepMutation, {
    props({ ownProps: { service }, mutate }) {
      return {
        completeProductionStep(stepType) {
          return mutate({
            variables: {
              serviceId: service.id,
              stepType
            }
          });
        }
      };
    }
  }),
  graphql(AddCommentMutation, {
    props({ ownProps: { match: { params: { jobId, serviceId } } }, mutate }) {
      return {
        addComment({ serviceId, input }) {
          return mutate({
            variables: { serviceId, input },
            update: (store, { data: { addComment } }) => {
              addToCache(addComment, store, "activities", {
                query: ServiceActivitiesQuery,
                variables: { serviceId }
              });
              addToCache(addComment, store, "activities", {
                query: JobActivitiesQuery,
                variables: { jobId }
              });
            }
          });
        }
      };
    }
  }),
  graphql(ChangeStatusMutation, {
    props({ ownProps: { service }, mutate }) {
      return {
        cancelService() {
          return mutate({
            variables: {
              id: service.id,
              input: {
                status: "cancelled"
              }
            }
          });
        },
        updateService(input) {
          return mutate({
            variables: {
              id: service.id,
              input
            }
          });
        }
      };
    }
  })
);

export default enhance(ServiceSidebar);



// WEBPACK FOOTER //
// ./src/components/services/ServiceSidebar.js